export const FEATURES_KEY = 'hm_travel_features';

/* Each supplier needs the site's OWN key (saved in its database record).
 * Until a site has one, every visitor-facing surface of that supplier — the
 * home hotel rows, hotel and flight search, the menus, hotel pages — behaves
 * as if the module were off. The server sends which suppliers are ready
 * before the first render (window.__HM_SUPPLIERS__ in the HTML; public-config
 * `suppliersReady` confirms it). The admin panel is NOT gated: the owner must
 * still reach API Integrations to add the key. */
const isAdminPath = () => {
    try { return /^\/(private|admin|onboarding)(\/|$)/.test(window.location.pathname); } catch { return false; }
};
// supplier → the feature flags it powers
const SUPPLIER_FEATURES = {
    ratehawk:  ['ratehawk', 'contentApi'],
    webbeds:   ['webbeds'],
    hotelbeds: ['hotelbeds'],
    flights:   ['flights'],
};
export function readSupplierReadiness() {
    if (typeof window === 'undefined') return {};
    const map = (window.__HM_SUPPLIERS__ && typeof window.__HM_SUPPLIERS__ === 'object') ? { ...window.__HM_SUPPLIERS__ } : {};
    if (window.__HM_RH_READY__ === false) map.ratehawk = false;
    return map;
}
export function withSupplierReadiness(features) {
    if (typeof window === 'undefined' || !features || isAdminPath()) return features;
    const ready = readSupplierReadiness();
    let out = features;
    for (const [supplier, keys] of Object.entries(SUPPLIER_FEATURES)) {
        if (ready[supplier] !== false) continue;
        for (const k of keys) {
            if (out[k] === false) continue;
            if (out === features) out = { ...features };
            out[k] = false;
        }
    }
    return out;
}
// Kept for existing callers/tests.
export const withRateHawkReadiness = withSupplierReadiness;

export function readSiteFeatures() {
    // Prefer the server-injected bootstrap value (available on first render, no API round-trip).
    if (typeof window !== 'undefined' && window.__HM_FEATURES__ && Object.keys(window.__HM_FEATURES__).length > 0) {
        window.__HM_FEATURES_READY__ = true;
        return withSupplierReadiness(window.__HM_FEATURES__);
    }
    try {
        const parsed = JSON.parse(localStorage.getItem(FEATURES_KEY) || 'null');
        // If localStorage has real features, mark as ready immediately
        if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
            window.__HM_FEATURES_READY__ = true;
            return withSupplierReadiness(parsed);
        }
        // Nothing available yet — return empty object, NOT ready
        return {};
    } catch {
        return {};
    }
}

export function isFeatureEnabled(features, key) {
    if (!key) return true;
    const hasAnyKeys = features && Object.keys(features).length > 0;
    if (!hasAnyKeys) {
        // Empty means one of two things:
        // 1. Features not loaded yet (incognito / first visit) → hide optional sections
        //    so we never flash wrong content. They reveal once the API responds.
        // 2. Site confirmed loaded but genuinely has no features configured → show all.
        // window.__HM_FEATURES_READY__ distinguishes the two cases.
        return typeof window !== 'undefined' && !!window.__HM_FEATURES_READY__;
    }
    return features[key] === true;
}

// Hotel routes are accessible if EITHER ratehawk OR webbeds is enabled.
// We use a special composite key 'hotelsAny' resolved via the helper below.
const FEATURE_ROUTE_MAP = [
    ['/hotels-partner', 'offlineHotels'],
    ['/hotel-detail-data', 'hotelsAny'],
    ['/confirm-booking', 'hotelsAny'],
    ['/search', 'hotelsAny'],
    ['/hajj-umrah', 'hajjUmrah'],
    ['/ummrah-planner', 'ummrahPlanner'],
    ['/nearby', 'nearbyHotels'],
    ['/deals', 'deals'],
    // /pkfare-flights must precede /flights only in specificity, not order —
    // prefixes are matched exactly or with a trailing slash, so both are safe.
    ['/pkfare-flights', 'pkfareFlights'],
    ['/flights', 'flights'],
    ['/tours', 'tours'],
    // /experiences shares the `tours` feature (Viator-powered).
    ['/experiences', 'tours'],
    ['/blog', 'blog'],
    ['/visa', 'visa'],
    ['/support', 'support'],
    ['/destinations', 'destinations'],
    ['/webbeds', 'webbeds'],
    ['/webbeds-hotel', 'webbeds'],
    ['/webbeds-book', 'webbeds'],
    ['/hotels', 'hotelsAny'],
    // B2B agent portal — gated by the `b2b` feature.
    ['/b2b', 'b2b'],
];

// Returns true when at least one hotel provider is enabled
export function isAnyHotelsEnabled(features) {
    return isFeatureEnabled(features, 'ratehawk')
        || isFeatureEnabled(features, 'webbeds')
        || isFeatureEnabled(features, 'hotelbeds');
}

export function getFeatureForPath(pathname = '') {
    if (!pathname || !pathname.startsWith('/')) return null;

    const match = FEATURE_ROUTE_MAP.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`));
    return match ? match[1] : null;
}

export function isPathEnabled(pathname, features) {
    const featureKey = getFeatureForPath(pathname);
    if (featureKey === 'hotelsAny') return isAnyHotelsEnabled(features);
    return isFeatureEnabled(features, featureKey);
}

export function sanitizeFeatureLinkedSettings(storageKey, value, features) {
    if (!value || typeof value !== 'object') return value;

    const sanitizeLink = (link) => {
        if (typeof link !== 'string') return link;
        const trimmed = link.trim();
        if (!trimmed.startsWith('/')) return trimmed;
        return isPathEnabled(trimmed, features) ? trimmed : '';
    };

    const sanitizeCta = (input) => {
        if (!input || typeof input !== 'object') return input;
        const nextLink = sanitizeLink(input.ctaLink);
        if (nextLink === input.ctaLink) return input;
        return {
            ...input,
            ctaLink: nextLink,
            ctaText: nextLink ? input.ctaText : '',
        };
    };

    const sanitizeNotice = (input) => {
        if (!input || typeof input !== 'object') return input;
        const nextLink = sanitizeLink(input.link);
        if (nextLink === input.link) return input;
        return {
            ...input,
            link: nextLink,
            linkText: nextLink ? input.linkText : '',
        };
    };

    const sanitizeFooter = (input) => {
        if (!input || typeof input !== 'object') return input;

        const filterLinks = (links = []) => links.filter((item) => {
            if (!item || typeof item !== 'object') return false;
            if (typeof item.url !== 'string') return true;
            return !!sanitizeLink(item.url) || !item.url.trim().startsWith('/');
        });

        return {
            ...input,
            columns: Array.isArray(input.columns)
                ? input.columns
                    .map((column) => ({
                        ...column,
                        links: filterLinks(column?.links),
                    }))
                    .filter((column) => Array.isArray(column.links) && column.links.length > 0)
                : input.columns,
            bottomLinks: Array.isArray(input.bottomLinks) ? filterLinks(input.bottomLinks) : input.bottomLinks,
        };
    };

    if (storageKey === 'hm_visitor_popup' || storageKey === 'hm_about_page') {
        return sanitizeCta(value);
    }

    if (storageKey === 'hm_notice_bar') {
        return sanitizeNotice(value);
    }

    if (storageKey === 'hm_footer_content') {
        return sanitizeFooter(value);
    }

    if (storageKey.startsWith('hm_page_')) {
        return sanitizeCta(value);
    }

    return value;
}
/* Is the floating WhatsApp button switched on for this site?
 *
 * Deliberately NOT isFeatureEnabled(). That helper answers "is this module
 * switched ON" and requires === true, so a key absent from the map reads as
 * off — correct for a module that must stay hidden until granted, wrong here.
 * This flag hides something the site ALREADY shows, and every tenant's stored
 * feature map was written before the key existed, so a strict check would have
 * pulled the button off every live site the moment it shipped, and flashed it
 * off again on each first paint until the features API answered.
 *
 * `!== false` mirrors the server's own rule (getEffectiveSiteFeatures) and
 * fails in the safe direction: visible unless an admin deliberately turned it
 * off. Lives here rather than in the component so it is testable — the
 * component imports react-router-dom, which CRA's jest cannot resolve. */
export function isWhatsAppButtonEnabled(features = readSiteFeatures()) {
    return features.whatsappButton !== false;
}
